import { f as isValidInteraction, u as Interaction, m as makeSymbol, o as makeCollection, S as SQUARE, i as isRemark, s as splitBySquare, h as toVersion } from './shared/minimark.20470752.mjs';
import { m as unwrapJSON, k as unwrapURI, h as toLowerCase, l as lowerTrim, n as wrapToString, w as wrapURI, j as isEmptyString, d as decodeHex } from './shared/minimark.92cd751b.mjs';
import { nanoid } from 'nanoid';
import '@polkadot/util';
import '@polkadot/keyring';
import 'slugify';

function checkBase({ symbol, parts, themes }) {
  if (symbol.includes(".") || symbol.includes("-")) {
    throw new Error("Symbol must not use dashes or dots");
  }
  if (Array.isArray(parts) && parts.some((part) => !part.id)) {
    throw new Error("Id is required for part");
  }
  if (themes && !Object.keys(themes).includes("default")) {
    throw new Error("Missing default key for theme");
  }
}
function isTransferable(nft, currentBlock) {
  const block = Number(nft.blockNumber);
  return nft.transferable === 1 || nft.transferable < 0 && block - nft.transferable >= currentBlock || nft.transferable > 1 && currentBlock >= nft.transferable;
}

const toInteraction = (interaction) => {
  isValidInteraction(interaction);
  return interaction;
};
const resolveValue = (interaction, id, restValues) => {
  switch (interaction) {
    case Interaction.ACCEPT:
      return {
        id,
        entity_type: restValues[0],
        entity_id: restValues[1]
      };
    case Interaction.BASE:
      return {
        value: unwrapJSON(id)
      };
    case Interaction.EQUIP:
      return {
        id,
        baseslot: restValues[0]
      };
    case Interaction.EQUIPPABLE:
      return {
        id,
        slot: restValues[0],
        value: restValues[1]
      };
    case Interaction.LOCK:
      return {
        id
      };
    case Interaction.RESADD:
      return {
        id,
        value: unwrapJSON(restValues[0]),
        replace: restValues[1]
      };
    case Interaction.SETPRIORITY:
      return {
        id,
        value: unwrapURI(restValues[0])
      };
    case Interaction.SETPROPERTY:
      return {
        id,
        name: unwrapURI(restValues[0]),
        value: unwrapURI(restValues[1])
      };
    case Interaction.THEMEADD:
      return {
        base_id: id,
        name: restValues[0],
        value: unwrapJSON(restValues[1])
      };
    case Interaction.BUY:
      return {
        id,
        recipient: restValues[0]
      };
    case Interaction.EMOTE:
      return {
        namespace: id,
        id: restValues[0],
        value: restValues[1]
      };
    case Interaction.SEND:
      return {
        id,
        recipient: restValues[0]
      };
    case Interaction.LIST:
      return {
        id,
        price: restValues[0]
      };
    case Interaction.CHANGEISSUER:
      return {
        id,
        value: restValues[0]
      };
    case Interaction.CREATE:
      return {
        value: unwrapJSON(id)
      };
    case Interaction.MINT:
      return {
        value: unwrapJSON(id),
        recipient: restValues[0]
      };
    case Interaction.BURN:
      return {
        id
      };
  }
};
const resolveRoyalty = (properties) => {
  if (!properties) {
    return void 0;
  }
  const royaltyInfo = properties.royaltyInfo;
  if (!royaltyInfo) {
    return void 0;
  }
  const { value } = royaltyInfo;
  if (!value) {
    return void 0;
  }
  return {
    receiver: value.receiver,
    percent: Number(value.royaltyPercentFloat)
  };
};
const makeRoyalty = (royalty) => {
  if (!royalty || !royalty.receiver || !royalty.percent) {
    return void 0;
  }
  if (royalty.percent < 0 || royalty.percent > 100) {
    console.warn("[MINIMARK] Royalty percent should be between 0 and 100");
    return void 0;
  }
  return {
    type: "royalty",
    value: {
      receiver: royalty.receiver,
      royaltyPercentFloat: royalty.percent
    }
  };
};
const resolveEquippable = (value) => {
  const operation = value[0];
  const offset = Number(["+", "-", "*"].includes(operation));
  const collections = value.slice(offset).split(",").filter(Boolean);
  if (operation === "*" || !offset && collections.length) {
    return {
      operation: "*",
      collections: !offset ? collections : ["*"]
    };
  }
  return {
    operation,
    collections
  };
};
const makeProperty = (value, type) => {
  return {
    type: type || typeof value === "number" ? "int" : "string",
    value
  };
};
const convertAttributesToProperties = (attributes) => {
  const properties = {};
  attributes.filter((attr) => attr.trait_type).forEach((attribute) => {
    properties[attribute.trait_type] = makeProperty(attribute.value);
  });
  return properties;
};
const mergeProperties = (properties, key, value) => {
  return {
    ...properties,
    [key]: value
  };
};

const toSerialNumber = (index, offset = 0, plusOne = true) => {
  return String(index + offset + Number(plusOne)).padStart(8, "0");
};
const makeBaseSymbol = (symbol) => {
  return !symbol ? toLowerCase(nanoid(13)) : lowerTrim(symbol, true);
};
const toNFTId = (nft, blockNumber) => {
  const { collection, symbol: instance, sn } = nft;
  if (!collection || !instance || !sn) {
    throw new ReferenceError("[APP] toNFTId: invalid nft");
  }
  return `${blockNumber}-${collection}-${instance}-${sn}`;
};
const toBaseId = (symbol, blockNumber) => {
  if (!blockNumber || !symbol) {
    throw new EvalError("[MINIMARK] unable to construct Base ID since block or symbol is missing");
  }
  return `base-${blockNumber}-${symbol}`;
};
const toThemeId = (baseId, name) => {
  if (!baseId || !name) {
    throw new EvalError("[MINIMARK] unable to construct Theme ID since baseId or name is missing");
  }
  return baseId.replace("base", "theme") + "-" + name;
};
const toPartId = (baseId, name) => {
  if (!baseId || !name) {
    throw new EvalError("[MINIMARK] unable to construct Part ID since baseId or name is missing");
  }
  return `${baseId}.${name}`;
};
const baseIdFromPartId = (partId) => {
  return partId.split(".")[0];
};
const toPropertyId = (nftId, key, value) => {
  if (!nftId || !key || !value) {
    throw new EvalError("[MINIMARK] unable to construct Property ID since nftId, key or value is missing");
  }
  return `${nftId}-${key}-${value}`;
};

const filterEmpty = (field) => !isEmptyString(field);
const createInteraction = ({ action, payload }) => {
  const convert = (props) => {
    const args = props.filter(filterEmpty).join(SQUARE);
    return `RMRK::${action}::2.0.0::${args}`;
  };
  switch (action) {
    case Interaction.ACCEPT:
      return convert([payload.id, payload.entity_type, payload.entity_id]);
    case Interaction.BASE:
      return convert([wrapToString(payload.value)]);
    case Interaction.BUY:
      return convert([payload.id, payload.recipient ?? ""]);
    case Interaction.CHANGEISSUER:
      return convert([payload.id, payload.value]);
    case Interaction.BURN:
      return convert([payload.id]);
    case Interaction.CREATE:
      return convert([wrapToString(payload.value)]);
    case Interaction.EMOTE:
      return convert([payload.namespace, payload.id, payload.value]);
    case Interaction.EQUIP:
      return convert([payload.id, payload.baseslot]);
    case Interaction.EQUIPPABLE:
      return convert([payload.id, payload.slot, payload.value]);
    case Interaction.LIST:
      return convert([payload.id, payload.price]);
    case Interaction.LOCK:
      return convert([payload.id]);
    case Interaction.MINT:
      return convert([wrapToString(payload.value), payload.recipient ?? ""]);
    case Interaction.RESADD:
      return convert([payload.id, wrapToString(payload.value), payload.replace]);
    case Interaction.SEND:
      return convert([payload.id, payload.recipient]);
    case Interaction.SETPROPERTY:
      return convert([payload.id, wrapURI(payload.name), wrapURI(payload.value)]);
    case Interaction.SETPRIORITY:
      return convert([payload.id, wrapToString(payload.value)]);
    case Interaction.THEMEADD:
      return convert([payload.base_id, payload.name, wrapToString(payload.value)]);
    default:
      throw new Error(`Unsupported action: ${action}`);
  }
};
const createNFT = (index, collectionId, name, metadata, transferable = 1, royalty, caller) => {
  const sn = toSerialNumber(index);
  const instance = makeSymbol(name);
  const royaltyInfo = makeRoyalty(royalty);
  return {
    name,
    // KodaFlavour, not required by RMRK v2
    sn,
    transferable,
    collection: collectionId,
    metadata,
    symbol: instance,
    properties: royaltyInfo ? { royaltyInfo } : void 0,
    currentOwner: caller
  };
};
const createMultipleItem = (max, caller, collectionId, name, metadata, offset = 0, updateName, transferable, royalty) => {
  return Array(max).fill(null).map(
    (_, i) => createNFT(
      i + offset,
      collectionId,
      updateName ? updateName(name, i) : name,
      metadata,
      transferable,
      royalty,
      caller
    )
  );
};
const createCollection = (caller, symbol, name, metadata, max = 0) => {
  return makeCollection(caller, symbol, name ?? "", metadata, max);
};
const createBase = (props) => {
  const { symbol: providedSymbol, parts = [], themes } = props;
  const symbol = makeBaseSymbol(providedSymbol);
  checkBase({ symbol, parts, themes });
  return {
    ...props,
    symbol
  };
};
const createResource = (_obj) => {
  console.warn("createResource is not implemented yet");
  return null;
};

const unwrap = (text) => {
  const decoded = unwrapURI(decodeHex(text));
  if (!isRemark(decoded)) {
    throw new TypeError(`RMRK: Unable to unwrap object ${decoded}`);
  }
  const [, mayInteraction, mayVersion, id, ...rest] = splitBySquare(decoded);
  const interaction = toInteraction(mayInteraction);
  const value = resolveValue(interaction, id, rest);
  return {
    interaction,
    value,
    version: toVersion(mayVersion)
  };
};

export { Interaction, baseIdFromPartId, checkBase, convertAttributesToProperties, createBase, createCollection, createInteraction, createMultipleItem, createNFT, createResource, isTransferable, makeProperty, makeRoyalty, mergeProperties, resolveEquippable, resolveRoyalty, resolveValue, toBaseId, toInteraction, toNFTId, toPartId, toPropertyId, toThemeId, unwrap as unwrapRemarkV2 };
